/**
 * Utility functions for search operations across modals
 */
import React from 'react';

/**
 * Formats search value for API calls
 * Returns '%' if value is empty (for wildcard search)
 * Trims whitespace from non-empty values
 */
export const formatSearchValue = (value: string): string => {
  const trimmedValue = value?.trim() || '';
  return trimmedValue || '%';
};

/**
 * Type-safe search parameter builders for specific APIs
 */
export const buildSearchParams = {
  // For location searches - returns proper locationName structure
  location: (searchValue: string): { locationName: string } => ({
    locationName: formatSearchValue(searchValue)
  }),
  
  // For website searches - returns proper name structure
  website: (searchValue: string): { name: string } => ({
    name: formatSearchValue(searchValue)
  }),
  
  // For product searches - returns proper productName structure
  product: (searchValue: string): { productName: string } => ({
    productName: formatSearchValue(searchValue)
  }),
  
  // For vendor searches
  vendor: (searchValue: string): { vendorName: string } => ({
    vendorName: formatSearchValue(searchValue)
  }),
  
  // For brand searches
  brand: (searchValue: string): { brandName: string } => ({
    brandName: formatSearchValue(searchValue)
  })
};

/**
 * Formats search parameters for different search types (Legacy - use buildSearchParams for type safety)
 */
export const formatSearchParams = {
  // For location searches
  location: (searchValue: string, field: string = 'locationName') => ({
    [field]: formatSearchValue(searchValue)
  }),
  
  // For product searches  
  product: (searchValue: string, field: string = 'productName') => ({
    [field]: formatSearchValue(searchValue)
  }),
  
  // For vendor searches
  vendor: (searchValue: string, field: string = 'vendorName') => ({
    [field]: formatSearchValue(searchValue)
  }),
  
  // For brand searches
  brand: (searchValue: string, field: string = 'brandName') => ({
    [field]: formatSearchValue(searchValue)
  }),
  
  // For website searches
  website: (searchValue: string, field: string = 'websiteName') => ({
    [field]: formatSearchValue(searchValue)
  }),
  
  // Generic search parameter formatter
  generic: (searchValue: string, fieldName: string) => ({
    [fieldName]: formatSearchValue(searchValue)
  })
};

/**
 * Creates a standardized search handler for modals
 */
export const createSearchHandler = <T>(
  searchFunction: (params: any) => Promise<T>,
  getSearchParams: (searchValue: string) => any,
  onError?: (error: any) => void
) => {
  return async (searchValue: string) => {
    try {
      const params = getSearchParams(searchValue);
      return await searchFunction(params);
    } catch (error) {
      console.error('Search failed:', error);
      if (onError) {
        onError(error);
      }
      throw error;
    }
  };
};

/**
 * Hook for standardized search state management
 */
export const useSearchState = (initialValue: string = '') => {
  const [searchValue, setSearchValue] = React.useState(initialValue);
  const [isSearching, setIsSearching] = React.useState(false);
  
  const handleSearch = async (searchFn: (value: string) => Promise<any>) => {
    setIsSearching(true);
    try {
      await searchFn(searchValue);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleClear = () => {
    setSearchValue('');
  };
  
  return {
    searchValue,
    setSearchValue,
    isSearching,
    handleSearch,
    handleClear,
    formatValue: () => formatSearchValue(searchValue)
  };
};